import { useState } from 'react';
import { Link } from 'react-router-dom';

const API = process.env.REACT_APP_API_URL || 'https://hs.aida-advisory.com';

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
  'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
  'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC'
];

export default function Signup() {
  const [form, setForm] = useState({
    school_name: '',
    state: '',
    city: '',
    admin_name: '',
    email: '',
    password: '',
    confirm_password: ''
  });
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const urlParams = new URLSearchParams(window.location.search);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (form.password !== form.confirm_password) {
      setError('Passwords do not match');
      return;
    }

    if (form.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setLoading(true);
    try {
      // Programs are invoiced by hand rather than charged at signup, so this
      // records a request instead of opening a checkout session.
      const res = await fetch(`${API}/api/inquiries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          kind: 'program',
          name: form.admin_name,
          email: form.email,
          organization: form.school_name,
          state: form.state,
          message: `Program signup request — ${form.school_name}`
            + (form.city ? `, ${form.city}` : '')
            + (form.state ? `, ${form.state}` : ''),
        })
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Could not send your request');
      }

      setSubmitted(true);
      setLoading(false);

    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center px-4 py-8">
        <div className="max-w-md w-full text-center">
          <div className="text-green-400 text-4xl mb-4">✓</div>
          <h1 className="text-2xl font-bold text-white mb-3">Request received</h1>
          <p className="text-gray-300 text-sm leading-relaxed">
            Thanks — we have your details and will be in touch shortly to get your
            program set up and send an invoice. No payment is needed right now.
          </p>
          <a href="https://aida-advisory.com/hs/" className="btn-secondary inline-block mt-6">
            Back to SchemeSync HS
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center px-4 py-8 md:py-12" style={{paddingTop:'max(2rem, env(safe-area-inset-top))',paddingBottom:'max(2rem, env(safe-area-inset-bottom))'}}>
      <div className="max-w-lg w-full">
        {/* Logo / Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-white tracking-tight">
            Scheme<span className="text-blue-400">Sync</span>
          </h1>
          <p className="text-gray-400 mt-2">High School Scouting Platform</p>
          <p className="text-gray-500 text-sm mt-1">Powered by AiDA Advisory</p>
        </div>

        {/* Signup Card */}
        <div className="bg-gray-800 rounded-xl shadow-2xl p-8 border border-gray-700">
          <h2 className="text-xl font-semibold text-white mb-2">Register Your Program</h2>
          <p className="text-gray-400 text-sm mb-4">Tell us about your program and we&rsquo;ll get you set up. Nothing is charged here &mdash; we invoice you directly.</p>
          <div className="bg-blue-900/30 border border-blue-700/30 rounded-lg px-3 py-2 mb-6 text-sm text-blue-300 flex items-center justify-between">
            <span>Program Subscription</span>
            <span className="font-bold text-white">$200/year</span>
          </div>

          {error && (
            <div className="bg-red-900/50 border border-red-700 text-red-300 px-4 py-3 rounded-lg mb-4 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* School Info Section */}
            <div className="border-b border-gray-700 pb-4 mb-4">
              <h3 className="text-sm font-semibold text-gray-300 uppercase tracking-wider mb-3">School Information</h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">School / Program Name</label>
                  <input
                    type="text"
                    name="school_name"
                    value={form.school_name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Lincoln High School"
                    required
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">City</label>
                    <input
                      type="text"
                      name="city"
                      value={form.city}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Atlanta"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">State</label>
                    <select
                      name="state"
                      value={form.state}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    >
                      <option value="">Select state</option>
                      {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Admin Account Section */}
            <div>
              <h3 className="text-sm font-semibold text-gray-300 uppercase tracking-wider mb-3">Administrator Account</h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Your Full Name</label>
                  <input
                    type="text"
                    name="admin_name"
                    value={form.admin_name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Coach Davis"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="coach@school.edu"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Password</label>
                  <input
                    type="password"
                    name="password"
                    value={form.password}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Minimum 8 characters"
                    required
                    minLength={8}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Confirm Password</label>
                  <input
                    type="password"
                    name="confirm_password"
                    value={form.confirm_password}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Confirm your password"
                    required
                    minLength={8}
                  />
                </div>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 px-4 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-800 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors duration-200 mt-4"
            >
              {loading ? 'Sending…' : 'Request Access'}
            </button>
          </form>

          <p className="text-center text-gray-400 text-sm mt-4">
            Already have an account?{' '}
            <Link to="/login" className="text-blue-400 hover:text-blue-300 font-medium">Sign In</Link>
          </p>
        </div>

        <p className="text-center text-gray-500 text-xs mt-6">
          No card required. By requesting access you agree to our{' '}
          <a href="https://aida-advisory.com/terms.html" target="_blank" rel="noopener noreferrer" className="text-gray-400 underline">Terms of Service</a>{' '}
          and{' '}
          <a href="https://aida-advisory.com/privacy.html" target="_blank" rel="noopener noreferrer" className="text-gray-400 underline">Privacy Policy</a>
        </p>
      </div>
    </div>
  );
}
